/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  CompoundEntityRef,
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  UserEntity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import { useApiHolder } from '@backstage/core-plugin-api';
import {
  EntityRefLink,
  EntityRefLinks,
  catalogApiRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { Box, Chip, Grid, useTheme } from '@material-ui/core';
import React from 'react';
import { useAsync } from 'react-use';
import { AboutField } from './AboutField';
import { TeamsChatLink, teamsChatUserOf } from '../../TeamsChat';
import { GroupMailLink } from '../../GroupMail';
import { MoreInTooltip } from '../../MoreInTooltip';
import { cloudAccountIdsOf, formatUpdated, platformTeamRef } from '../utils';
import { EntityRefLinkWithVuid, EntityRefLinksWithVuid } from '../../VuidChip';

/**
 * How the two `spec.managementMode` values of a product read on the page. An
 * unknown value is shown as it stands rather than swallowed, so that a new
 * CMDB mode is visible here before anybody teaches this table about it.
 */
const MANAGEMENT_MODES: Record<
  string,
  { label: string; description: string } | undefined
> = {
  full: {
    label: 'Full',
    description:
      'The platform team sets up and runs the infrastructure for this product.',
  },
  inventory: {
    label: 'Inventory only',
    description:
      'Nothing is set up for this product. It only keeps a record of cloud accounts that already exist, so the platform team does not run them.',
  },
};

/**
 * What each kind of contact of a team or a department is there for, as the
 * CMDB defines them - shown on an info icon next to the row. The same person
 * can hold several of them. A system lists contacts too, from another source
 * and not by these rules, so it gets none of this.
 */
function contactDescriptions(unit: 'team' | 'department') {
  return {
    accountable: `Accountable for the ${unit}, and the next to be contacted or notified when the first contacts cannot help - usually its line manager and disciplinary lead. Responsible for its budget and decides who gets access to its resources: Entra groups, cloud accounts, GitLab.`,
    main: `The central contact of the ${unit}, the first to be contacted or notified - e.g. its product owner, product manager or team lead.`,
    technical:
      unit === 'team'
        ? 'The technical contacts of the team, e.g. its tech lead - among the first to be contacted or notified.'
        : 'The technical contacts of the department, if it has any - among the first to be contacted or notified.',
    security: `Who to turn to with security matters - among the first to be contacted or notified. Need not be a member of the ${unit}: one person can cover several teams or departments. Where none is set, the technical contacts stand in.`,
  };
}

/**
 * Props for {@link AboutContent}.
 *
 * @public
 */
export interface AboutContentProps {
  entity: Entity;
}

/** @public */
export function AboutContent(props: Readonly<AboutContentProps>) {
  const { entity } = props;
  const isSystem = entity.kind.toLocaleLowerCase('en-US') === 'system';
  const isResource = entity.kind.toLocaleLowerCase('en-US') === 'resource';
  const isComponent = entity.kind.toLocaleLowerCase('en-US') === 'component';
  const isAPI = entity.kind.toLocaleLowerCase('en-US') === 'api';
  const isProduct = entity.kind.toLocaleLowerCase('en-US') === 'product';
  const isTemplate = entity.kind.toLocaleLowerCase('en-US') === 'template';
  const isLocation = entity.kind.toLocaleLowerCase('en-US') === 'location';
  const isGroup = entity.kind.toLocaleLowerCase('en-US') === 'group';
  const isTeam = isGroup && entity.spec?.type === 'team';
  const isDepartment = isGroup && entity.spec?.type === 'department';
  const updated = formatUpdated(entity.spec?.updated as string | undefined);

  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const partOfComponentRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    {
      kind: 'component',
    },
  );
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);
  const technicalContacts = extractContacts(entity, 'technicalContacts');
  const mainContacts = extractContacts(entity, 'mainContacts');
  const securityContacts = extractContacts(entity, 'securityContacts');
  const accountableContact = extractAccountableContact(entity);
  let contactHelp: ReturnType<typeof contactDescriptions> | undefined;
  if (isTeam) {
    contactHelp = contactDescriptions('team');
  } else if (isDepartment) {
    contactHelp = contactDescriptions('department');
  }
  const chatUserOf = useTeamsChatUsers([
    ...(accountableContact ? [accountableContact] : []),
    ...mainContacts,
    ...technicalContacts,
    ...securityContacts,
  ]);
  // What a group chat with the contacts of a list is named after.
  const entityTitle = entity.metadata.title ?? entity.metadata.name;
  const platformTeam: CompoundEntityRef | undefined = platformTeamRef(entity);
  const managementMode = entity.spec?.managementMode as string | undefined;
  const vuid = extractCmdbVuid(entity);
  const sliceCluster = extractSliceCluster(entity);
  const instance = entity.spec?.instance as string | undefined;

  return (
    <Grid container>
      {!isGroup && (
        <AboutField
          // What owns a product is the team that builds it, which is what the
          // CMDB and the rest of the page call it. Named so, it reads as the
          // pair it is with the platform team right next to it.
          label={isProduct ? 'Team' : 'Owner'}
          value={isProduct ? 'No Team' : 'No Owner'}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {ownedByRelations.length > 0 && (
            <EntityRefLinksWithVuid
              entityRefs={ownedByRelations}
              defaultKind="group"
            />
          )}
        </AboutField>
      )}
      {/* Next to the owner, because the two answer the same question from
          either side: the owner builds the product, the platform team runs
          what it runs on. Left out rather than shown empty when there is
          none - that only happens for a product from a v11 artifact, which
          knew no platform team at all. */}
      {platformTeam && (
        <AboutField
          label="Platform Team"
          description="The team that takes care of the infrastructure this product runs on. Ask them if something needs setting up or is broken. The owner team builds and operates the product itself."
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          <EntityRefLinkWithVuid entityRef={platformTeam} defaultKind="group" />
        </AboutField>
      )}
      {/* Right behind the platform team, because it says what that team does
          for this product - for an inventory product, nothing. */}
      {managementMode && (
        <AboutField
          label="Setup"
          value={MANAGEMENT_MODES[managementMode]?.label ?? managementMode}
          description={MANAGEMENT_MODES[managementMode]?.description}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        />
      )}
      {(isSystem || isTeam || isDepartment) && (
        <>
          <AboutField
            label="Accountable Contact"
            value="No Accountable Contact"
            description={contactHelp?.accountable}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          >
            {accountableContact && (
              <ContactLinks
                contacts={[accountableContact]}
                chatUserOf={chatUserOf}
              />
            )}
          </AboutField>
          {/* The CMDB knows no main contacts for a department */}
          {!isDepartment && (
            <AboutField
              label="Main Contacts"
              value="No Main Contacts"
              description={contactHelp?.main}
              gridSizes={{ xs: 12, sm: 6, lg: 4 }}
            >
              {mainContacts.length > 0 && (
                <ContactLinks
                  contacts={mainContacts}
                  chatUserOf={chatUserOf}
                  topicName={`${entityTitle} - Main Contacts`}
                />
              )}
            </AboutField>
          )}
          <AboutField
            label="Technical Contacts"
            value="No Technical Contacts"
            description={contactHelp?.technical}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          >
            {technicalContacts.length > 0 && (
              <ContactLinks
                contacts={technicalContacts}
                chatUserOf={chatUserOf}
                topicName={`${entityTitle} - Technical Contacts`}
              />
            )}
          </AboutField>
          {(isTeam || isDepartment) && (
            <AboutField
              label="Security Contacts"
              value="No Security Contacts"
              description={contactHelp?.security}
              gridSizes={{ xs: 12, sm: 6, lg: 4 }}
            >
              {securityContacts.length > 0 && (
                <ContactLinks
                  contacts={securityContacts}
                  chatUserOf={chatUserOf}
                  topicName={`${entityTitle} - Security Contacts`}
                />
              )}
            </AboutField>
          )}
        </>
      )}
      {isSystem && (
        <>
          <AboutField
            label="Business Criticality"
            value={(entity.spec as any).businessCriticality}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          />
          <AboutField
            label="Product Lifecycle"
            value={(entity.spec as any).productLifecycle}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          />
        </>
      )}
      {(isAPI || isComponent || partOfSystemRelations.length > 0) && (
        <AboutField
          label="System"
          value="No System"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {partOfSystemRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfSystemRelations}
              defaultKind="system"
            />
          )}
        </AboutField>
      )}
      {isComponent && partOfComponentRelations.length > 0 && (
        <AboutField
          label="Parent Component"
          value="No Parent Component"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          <EntityRefLinks
            entityRefs={partOfComponentRelations}
            defaultKind="component"
          />
        </AboutField>
      )}
      {!isTeam &&
        (isAPI ||
          isComponent ||
          isResource ||
          isTemplate ||
          isGroup ||
          isLocation ||
          typeof entity?.spec?.type === 'string') && (
          <AboutField
            label="Type"
            value={entity?.spec?.type as string}
            gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          />
        )}
      {/* Right behind the type, because together they say where a cluster or
          a slice actually runs - which nothing else on the page names. */}
      {sliceCluster && (
        <AboutField label="Cluster" gridSizes={{ xs: 12, sm: 6, lg: 4 }}>
          <EntityRefLinks
            entityRefs={[
              { kind: 'resource', namespace: 'default', name: sliceCluster },
            ]}
            defaultKind="resource"
          />
        </AboutField>
      )}
      {instance && (
        <AboutField
          label="Instance"
          description="The cluster management instance the cluster is run from, e.g. Rancher. A slice runs on the one of the cluster it is cut from."
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          <Link to={`https://${instance}`}>{instance}</Link>
        </AboutField>
      )}
      {(isAPI ||
        isComponent ||
        typeof entity?.spec?.lifecycle === 'string') && (
        <AboutField
          label="Lifecycle"
          value={entity?.spec?.lifecycle as string}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        />
      )}
      <AboutField
        label="BACKSTAGE-ID"
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        value={entity.metadata.name}
      />
      {cloudAccountIdsOf(entity).map(({ label, value, description }) => (
        <AboutField
          key={label}
          label={label}
          description={description}
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          value={value}
        />
      ))}
      {vuid && (
        <AboutField
          label="VUID"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
          value={vuid}
        />
      )}
      <AboutField
        label="Last Update"
        gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        value={updated.label}
        title={updated.title}
      />
      {entity?.metadata?.tags && (
        <AboutField
          label="Tags"
          value="No Tags"
          gridSizes={{ xs: 12, sm: 6, lg: 4 }}
        >
          {(entity?.metadata?.tags || []).map(t => (
            <Chip key={t} size="small" label={t} />
          ))}
        </AboutField>
      )}
    </Grid>
  );
}

/**
 * The CMDB vuid of the entity. Departments, teams and products carry one,
 * everything else leaves the field out.
 *
 * The only CMDB identifier the UI shows. `metadata.name` is the vuid too, so
 * the row repeats BACKSTAGE-ID today; it is shown all the same, because the
 * vuid is about to replace the uid and deserves a label of its own rather than
 * hiding behind a Backstage one. The uid stays where the code needs it - CMDB
 * paths, the model api - and out of sight everywhere else.
 */
function extractCmdbVuid(entity: Entity) {
  return entity?.metadata?.annotations?.['schema.dp.netrtl.com/vuid'] ?? '';
}

/**
 * The cluster a slice is cut from, which it carries as its only `deployedIn`
 * entry. A slice whose cluster is nowhere in the CMDB carries none, and
 * nothing that is not a slice gets the row.
 */
function extractSliceCluster(entity: Entity): string | undefined {
  if (entity.spec?.type !== 'k8s-cluster-slice') {
    return undefined;
  }
  return (entity.spec?.deployedIn as string[] | undefined)?.[0];
}

/**
 * A contact as the CMDB provider puts it on the entity: the user it is, and
 * the address it is reached at - which is also who it is to Teams.
 */
type Contact = { ref: CompoundEntityRef; mail?: string };

function toContact(contact: { entityRef: string; mail?: string }): Contact {
  return {
    ref: { kind: 'user', name: contact.entityRef, namespace: 'default' },
    mail: contact.mail,
  };
}

/**
 * One of the contact lists of the entity, e.g. mainContacts,
 * technicalContacts or the securityContacts of a department.
 */
function extractContacts(entity: Entity, field: string): Contact[] {
  const contacts = (entity?.spec?.[field] as any[] | undefined) ?? [];
  return contacts.filter(contact => contact.entityRef).map(toContact);
}

function extractAccountableContact(entity: Entity): Contact | undefined {
  const contact = entity.spec?.accountableContact as any;
  return contact?.entityRef ? toContact(contact) : undefined;
}

/** User names are compared the way the catalog compares them. */
const userKey = (name: string) => name.toLocaleLowerCase('en-US');

/**
 * Who each contact on the card is to Teams: their Entra sign-in name where
 * the catalog has the user, see teamsChatUserOf - for most people it differs
 * from the address the CMDB carries. One request for every contact on the
 * card. Where the catalog is not at hand or has no such user, the address
 * stands in, as it did before.
 */
function useTeamsChatUsers(
  contacts: Contact[],
): (contact: Contact) => string | undefined {
  const catalogApi = useApiHolder().get(catalogApiRef);
  const refs = [
    ...new Set(contacts.map(contact => stringifyEntityRef(contact.ref))),
  ].sort();
  // A fresh array on every render: the request is made again only when the
  // contacts change.
  const refsKey = refs.join(',');

  const { value: chatUsers } = useAsync(async () => {
    if (!catalogApi || refs.length === 0) {
      return new Map<string, string>();
    }
    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: refs,
      fields: ['metadata.name', 'spec.profile'],
    });
    return new Map(
      items.flatMap(user => {
        // `spec` is missing on a user without a profile: the catalog returns
        // only the fields asked for that exist.
        const chatUser = user?.spec
          ? teamsChatUserOf(user as UserEntity)
          : undefined;
        return user && chatUser
          ? [[userKey(user.metadata.name), chatUser] as const]
          : [];
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, refsKey]);

  return contact => chatUsers?.get(userKey(contact.ref.name)) ?? contact.mail;
}

/**
 * How many contacts a list shows before the rest goes behind `+N more`, as in
 * the department tree. A list of eight would otherwise make the card eight
 * lines taller.
 */
const MAX_VISIBLE_CONTACTS = 2;

/** One contact, with a chat in Teams where the catalog knows them there. */
function ContactLine(props: { contact: Contact; chatUser?: string }) {
  return (
    <Box display="flex" alignItems="center">
      <EntityRefLink entityRef={props.contact.ref} defaultKind="user" />
      {props.chatUser && <TeamsChatLink users={[props.chatUser]} />}
    </Box>
  );
}

/**
 * The contacts of one list, one per line, each with a chat in Teams - and,
 * where there are several, one chat with all of them, named after the list,
 * and one email to them all. Past the first few the names go behind
 * `+N more`; the chat and the email still reach everyone.
 *
 * Someone the CMDB lists twice is shown, chatted with and written to once.
 */
function ContactLinks(props: {
  contacts: Contact[];
  chatUserOf: (contact: Contact) => string | undefined;
  topicName?: string;
}) {
  const { chatUserOf, topicName } = props;
  const theme = useTheme();
  const contacts = props.contacts.filter(
    (contact, index, all) =>
      all.findIndex(
        other => userKey(other.ref.name) === userKey(contact.ref.name),
      ) === index,
  );
  const chatUsers = [
    ...new Set(
      contacts.flatMap(contact => {
        const chatUser = chatUserOf(contact);
        return chatUser ? [chatUser] : [];
      }),
    ),
  ];
  const mails = [
    ...new Set(
      contacts.flatMap(contact => (contact.mail ? [contact.mail] : [])),
    ),
  ];
  const lineOf = (contact: Contact) => (
    <ContactLine
      key={userKey(contact.ref.name)}
      contact={contact}
      chatUser={chatUserOf(contact)}
    />
  );
  const hidden = contacts.slice(MAX_VISIBLE_CONTACTS);
  return (
    <>
      {contacts.slice(0, MAX_VISIBLE_CONTACTS).map(lineOf)}
      {hidden.length > 0 && (
        // Under the names rather than the person icon in front of them: the
        // icon is 1em wide with a margin of half a spacing unit.
        <Box paddingLeft={`calc(1em + ${theme.spacing(0.5)}px)`}>
          <MoreInTooltip count={hidden.length}>
            {hidden.map(lineOf)}
          </MoreInTooltip>
        </Box>
      )}
      {(chatUsers.length > 1 || mails.length > 1) && (
        <Box display="flex" alignItems="center" flexWrap="wrap">
          {chatUsers.length > 1 && (
            <TeamsChatLink
              users={chatUsers}
              topicName={topicName}
              label="Group chat"
            />
          )}
          {mails.length > 1 && <GroupMailLink addresses={mails} />}
        </Box>
      )}
    </>
  );
}
